import { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  FirestoreError
} from 'firebase/firestore';
import { Status } from '../types';
import { migrateDefaultStatuses } from '../utils/migrateStatuses';
import { firestore } from '../../../firebase/config';

export const useStatuses = (workspaceId: string) => {
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const migrationCompleted = useRef(false);
  
  useEffect(() => {
    const checkAndMigrateStatuses = async () => {
      if (migrationCompleted.current) return;
      try {
        await migrateDefaultStatuses(firestore, workspaceId);
      } catch (error) {
        console.error('Error during status migration:', error);
      }
      migrationCompleted.current = true;
    };

    checkAndMigrateStatuses();
  }, [workspaceId]);

  useEffect(() => {
    const statusesRef = collection(firestore, `workspaces/${workspaceId}/statuses`);
    
    const unsubscribe = onSnapshot(
      statusesRef,
      (snapshot) => {
        const statusesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Status[];
        
        setStatuses(statusesData);
        setStatus('success');
      },
      (error: FirestoreError) => {
        console.error('Statuses error:', error);
        setStatus('error');
      }
    );

    return () => unsubscribe();
  }, [workspaceId]);

  const addStatus = async (data: Omit<Status, 'id'>): Promise<void> => {
    const statusesRef = collection(firestore, `workspaces/${workspaceId}/statuses`);
    await addDoc(statusesRef, data);
  };

  const updateStatus = async (id: string, data: Partial<Status>): Promise<void> => {
    const statusRef = doc(firestore, `workspaces/${workspaceId}/statuses/${id}`);
    await updateDoc(statusRef, data);
  };

  const deleteStatus = async (id: string): Promise<void> => {
    const statusRef = doc(firestore, `workspaces/${workspaceId}/statuses/${id}`);
    await deleteDoc(statusRef);
  };

  return {
    statuses,
    status,
    addStatus,
    updateStatus,
    deleteStatus
  };
};